/**
 * useErrorUsuario — separa el error TÉCNICO del mensaje que lee la persona.
 *
 * Hallazgo central de la auditoría de microcopy 2026-07-15:
 * el patrón `showToast(e?.message || 'texto amable', 'error')` hacía que el
 * mensaje amable NO se viera nunca, porque el error de Supabase/Postgres
 * SIEMPRE trae texto (en inglés). El camarero acababa leyendo cosas como
 * `new row violates row-level security policy for table "pagos"`.
 *
 * Regla del proyecto a partir de ahora:
 *
 *   ❌ showToast(e?.message || 'No se pudo cobrar', 'error')
 *   ✅ reportarError(e, { scope: 'salon.cobrarParte', sesionId })
 *      showToast('No se pudo cobrar esta parte. Reintenta o cóbrala en efectivo.', 'error')
 *
 * El técnico va a la consola y a Sentry. La persona lee siempre el texto que
 * hemos escrito nosotros, con la salida (qué hacer ahora) incluida.
 */

/** Contexto opcional que acompaña al error en Sentry (nunca se muestra en pantalla). */
export interface ContextoError {
  /** Dónde ocurrió, en formato `modulo.funcion`. Ej: `salon.confirmarPago`. */
  scope: string
  /** Cualquier dato útil para depurar (ids, importes, método de pago…). */
  [key: string]: unknown
}

/**
 * Manda el error a la consola y a Sentry. NO muestra nada al usuario:
 * el toast lo escribe quien llama, con texto humano.
 *
 * Es seguro llamarlo en SSR y con Sentry desactivado (no-op silencioso).
 */
export function reportarError(error: unknown, contexto: ContextoError): void {
  // Siempre a consola: es lo que ve el dev en local y en el inspector.
  console.error(`[${contexto.scope}]`, error, contexto)

  if (typeof window === 'undefined') return

  // Import dinámico: Sentry solo existe en cliente y puede estar deshabilitado.
  import('@sentry/vue')
    .then((Sentry) => {
      const err = error instanceof Error ? error : new Error(descripcionTecnica(error))
      Sentry.captureException(err, {
        tags: { scope: contexto.scope },
        extra: { ...contexto, originalError: error },
      })
    })
    .catch(() => {
      /* Sentry no cargó: el error ya está en consola, no rompemos el flujo. */
    })
}

/** Serializa cualquier cosa a un string legible para el canal técnico (Sentry/consola). */
export function descripcionTecnica(error: unknown): string {
  if (error instanceof Error) return error.message
  if (typeof error === 'string') return error
  if (error && typeof error === 'object') {
    const e = error as Record<string, unknown>
    const partes = [e.message, e.hint, e.details, e.code].filter(Boolean)
    if (partes.length) return partes.join(' · ')
    try {
      return JSON.stringify(error)
    } catch {
      return 'Error no serializable'
    }
  }
  return String(error)
}

/**
 * Decide si un `resultado.error` devuelto por uno de NUESTROS RPC se puede
 * enseñar tal cual, o si hay que usar el texto de respaldo.
 *
 * Nuestros RPC devuelven dos cosas distintas bajo la misma clave `error`:
 *   - Mensajes escritos para humanos: "Este comensal ya pagó su parte".
 *     → Son útiles y accionables. Se muestran.
 *   - Códigos técnicos: "sesion_no_encontrada", "token_invalido".
 *     → No significan nada para un camarero. Se sustituyen por `respaldo`.
 *
 * Regla práctica: si viene en snake_case, sin espacios, o parece inglés de
 * Postgres, es un código → gana el texto humano que escribimos nosotros.
 */
export function textoErrorRpc(raw: unknown, respaldo: string): string {
  if (typeof raw !== 'string') return respaldo
  const txt = raw.trim()
  if (!txt) return respaldo

  // Código técnico: snake_case (sesion_no_encontrada, token_invalido…).
  if (/^[a-z0-9]+(_[a-z0-9]+)+$/.test(txt)) return respaldo

  // Una sola palabra sin espacios tampoco es un mensaje ("null", "failed").
  if (!txt.includes(' ')) return respaldo

  // Jerga de base de datos que se cuela desde Postgres, aunque traiga espacios.
  const jerga = [
    'row-level security',
    'violates',
    'constraint',
    'duplicate key',
    'null value in column',
    'permission denied',
    'jwt',
    'relation ',
    'function ',
    'syntax error',
    'failed to fetch',
  ]
  const lower = txt.toLowerCase()
  if (jerga.some((j) => lower.includes(j))) return respaldo

  return txt
}
